import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useMutation } from '@apollo/client/react'

import { CrossIcon } from '@pleio/icons/cross'

import AddUserField from 'js/components/AddUserField'
import Button from 'js/components/Button'
import EntityList from 'js/components/EntityList/EntityList'
import Errors from 'js/components/Errors'
import Flexer from 'js/components/Flexer/Flexer'
import { H4 } from 'js/components/Heading'
import IconButton from 'js/components/IconButton'
import UserLink from 'js/components/UserLink'

import { INVITE_TO_EVENT } from './Invite.mutations'

type Props = {
    guid: string
    onComplete: () => void
}

const Invite = ({ guid, onComplete }: Props) => {
    const { t } = useTranslation()

    const [inviteToEvent, { loading, error }] = useMutation(INVITE_TO_EVENT)

    const [invites, setInvites] = useState([])

    const addInvite = (invite) => {
        setInvites((prev) => [...prev, invite])
    }

    const removeInvite = (index) => {
        setInvites((prev) => prev.filter((_, idx) => idx !== index))
    }

    const submit = async () => {
        try {
            await inviteToEvent({
                variables: {
                    input: {
                        guid,
                        users: invites.map(({ guid, email }) =>
                            guid ? { guid } : { email },
                        ),
                    },
                },
            })
            onComplete()
        } catch (caught) {
            console.error(caught)
            throw caught instanceof Error ? caught : new Error(String(caught))
        }
    }

    return (
        <>
            <AddUserField
                label={t('entity-event.add-invite')}
                users={invites}
                onAddUser={addInvite}
                allowEmail
                style={{ paddingTop: '16px' }}
            />

            {invites.length > 0 && (
                <>
                    <H4 style={{ marginTop: '16px' }}>
                        {t('entity-event.count-invite', {
                            count: invites.length,
                        })}
                    </H4>
                    <EntityList
                        style={{
                            overflowY: 'auto',
                        }}
                    >
                        {invites.map((invite, i) => {
                            return (
                                <Flexer
                                    key={i}
                                    style={{
                                        alignItems: 'center',
                                        justifyContent: 'space-between',
                                    }}
                                >
                                    <UserLink entity={invite} showEmail />

                                    <Flexer>
                                        <IconButton
                                            size="large"
                                            variant="secondary"
                                            onClick={() => removeInvite(i)}
                                            tooltip={t('action.remove')}
                                            aria-label={t(
                                                'event.remove-invite',
                                            )}
                                        >
                                            <CrossIcon />
                                        </IconButton>
                                    </Flexer>
                                </Flexer>
                            )
                        })}
                    </EntityList>
                </>
            )}

            <Errors errors={error} />

            <Flexer mt>
                <Button
                    size="normal"
                    variant="primary"
                    loading={loading}
                    disabled={invites.length === 0}
                    onClick={submit}
                >
                    {t('action.invite')}
                </Button>
            </Flexer>
        </>
    )
}

export default Invite
